import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import {
  Button,
  TextField,
  Box,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Divider,
  Alert,
  FormHelperText,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  Avatar,
  Fade,
  Skeleton,
  Checkbox,
  FormControlLabel,
  CircularProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  QrCodeScanner as QrCodeScannerIcon,
  Inventory as InventoryIcon,
  LocalOffer as LocalOfferIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { MarketingProductVariant } from '../../types/marketingTypes';
import { Category } from '../../types/category';
import promotionService from '../../services/marketingService';
import * as productService from '../../services/productService';
import { getCategories } from '../../services/categoryService';
import axiosInstance from '../../utils/axiosConfig';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`variant-tabpanel-${index}`}
      aria-labelledby={`variant-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// مكون إدخال متغير المنتج عن طريق الباركود
const BarcodeProductInput: React.FC<{
  onSelect: (variant: MarketingProductVariant) => void;
}> = ({ onSelect }) => {
  const [barcode, setBarcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleScan = async () => {
    if (!barcode.trim()) {
      setError('الرجاء إدخال باركود صحيح');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // استدعاء API للبحث عن متغير المنتج باستخدام الباركود
      const result = await promotionService.getVariantByBarcode(barcode);
      if (result) {
        onSelect(result);
        setBarcode('');
      } else {
        setError('لم يتم العثور على منتج بهذا الباركود');
      }
    } catch (err) {
      setError('حدث خطأ أثناء البحث');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TextField
          fullWidth
          label="أدخل الباركود أو امسح بالقارئ"
          value={barcode}
          onChange={(e) => setBarcode(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleScan();
            }
          }}
          variant="outlined"
          dir="rtl"
          InputProps={{
            startAdornment: (
              <QrCodeScannerIcon sx={{ color: 'action.active', mr: 1 }} />
            ),
          }}
          disabled={loading}
        />
        <Button
          variant="contained"
          onClick={handleScan}
          disabled={loading || !barcode.trim()}
          sx={{ mr: 2, height: '56px', minWidth: '100px' }}
          startIcon={<QrCodeScannerIcon />}
        >
          {loading ? 'جاري البحث...' : 'بحث'}
        </Button>
      </Box>
      
      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <LocalOfferIcon sx={{ fontSize: 16, mr: 0.5 }} />
        يمكنك إدخال الباركود يدوياً أو استخدام قارئ الباركود - سيتم إضافة المنتج مباشرة عند العثور عليه
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

// مكون اختيار متغير المنتج يدويًا
const ManualProductVariantSelector: React.FC<{
  onSelect: (variant: MarketingProductVariant) => void;
}> = ({ onSelect }) => {
  const [products, setProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [variants, setVariants] = useState<MarketingProductVariant[]>([]);
  const [loading, setLoading] = useState(false);

  // جلب قائمة المنتجات
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // زيادة حد جلب المنتجات إلى 1000 لتظهر جميع المنتجات
        const data = await productService.getProducts(1, 10000);
        setProducts(data.products || []);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    fetchProducts();
  }, []);

  // عند اختيار منتج، جلب متغيراته
  const handleProductChange = async (productId: string) => {
    setSelectedProduct(productId);
    
    if (productId) {
      try {
        setLoading(true);
        // استخدام خدمة التسويق لجلب متغيرات المنتج بالشكل المناسب
        const data = await promotionService.getProductVariants(productId);
        setVariants(data);
      } catch (error) {
        console.error('Error fetching variants:', error);
      } finally {
        setLoading(false);
      }
    } else {
      setVariants([]);
    }
  };

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="product-select-label">اختر المنتج</InputLabel>
        <Select
          labelId="product-select-label"
          value={selectedProduct || ''}
          onChange={(e) => handleProductChange(e.target.value as string)}
          label="اختر المنتج"
        >
          {products.map(product => (
            <MenuItem key={product._id} value={product._id}>
              {product.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedProduct && (
        <FormControl fullWidth>
          <InputLabel id="variant-select-label">اختر متغير المنتج</InputLabel>
          <Select
            labelId="variant-select-label"
            label="اختر متغير المنتج"
            disabled={loading}
            onChange={(e) => {
              const variant = variants.find(v => v._id === e.target.value);
              if (variant) onSelect(variant);
            }}
          >
            {variants.map(variant => (
              <MenuItem key={variant._id} value={variant._id}>
                {variant.attributes.map(attr => `${attr.name}: ${attr.value}`).join(', ')} - {variant.sku}
              </MenuItem>
            ))}
          </Select>
          {loading && <FormHelperText>جاري تحميل متغيرات المنتج...</FormHelperText>}
        </FormControl>
      )}
    </Box>
  );
};

// مكون استيراد متغيرات المنتج من ملف إكسل
const ExcelProductImporter: React.FC<{
  onImport: (variants: MarketingProductVariant[]) => void;
}> = ({ onImport }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
    }
  };

  const handleDownloadTemplate = () => {
    // إنشاء قالب Excel يحتوي على الباركود فقط
    const data = [
      ['الباركود'],
      ['1234567890123'],
      ['1234567890124'],
      ['1234567890125']
    ];

    // إنشاء workbook جديد
    const workbook = XLSX.utils.book_new();
    
    // إنشاء worksheet من البيانات
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    
    // تحديد عرض العمود
    worksheet['!cols'] = [{ width: 20 }];
    
    // إضافة worksheet إلى workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'الباركودات');
    
    // تحميل الملف
    XLSX.writeFile(workbook, 'قالب_الباركود.xlsx');
  };

  const handleImport = async () => {
    if (!file) {
      setError('الرجاء اختيار ملف أولاً');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      
      // استخدام FormData لرفع الملف
      const formData = new FormData();
      formData.append('file', file);
      
      // استدعاء API لاستيراد متغيرات المنتج من ملف إكسل
      const result = await promotionService.importProductVariantsFromExcel(formData);
      onImport(result.variants);
      setSuccessMessage(result.message || `تم استيراد ${result.variants.length} منتج بنجاح`);
      setFile(null); // إعادة تعيين الملف بعد النجاح
      
    } catch (err: any) {
      setError(err.message || 'حدث خطأ أثناء استيراد البيانات');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* زر تحميل القالب */}
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleDownloadTemplate}
          startIcon={<DownloadIcon />}
          sx={{ mb: 2 }}
        >
          تحميل قالب الإكسل
        </Button>
        <Typography variant="caption" display="block" color="text.secondary">
          حمل قالب Excel أولاً، املأه بالباركودات، ثم ارفعه هنا
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          p: 3,
          mb: 2,
          textAlign: 'center',
          borderStyle: 'dashed',
          borderColor: 'grey.400',
          bgcolor: 'grey.50'
        }}
      >
        <input
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          id="excel-file-upload"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="excel-file-upload">
          <Button
            variant="contained"
            component="span"
            startIcon={<CloudUploadIcon />}
          >
            اختر ملف إكسل
          </Button>
        </label>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          يدعم ملفات Excel (.xlsx) و CSV (.csv) - يجب أن يحتوي الملف على عمود واحد: الباركود
        </Typography>
        {file && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            الملف المختار: {file.name}
          </Typography>
        )}
      </Paper>
      
      {file && (
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={loading}
          fullWidth
        >
          {loading ? 'جاري الاستيراد...' : 'استيراد متغيرات المنتج'}
        </Button>
      )}
      
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {successMessage && <Alert severity="success" sx={{ mt: 2 }}>{successMessage}</Alert>}
    </Box>
  );
};

// مكون اختيار التصنيفات
const CategorySelector: React.FC<{
  selectedCategories: string[];
  onCategoriesChange: (categories: string[]) => void;
  onCategoryProductsLoad?: (variants: MarketingProductVariant[]) => void;
  isTargetPriceCampaign?: boolean;
}> = ({ selectedCategories, onCategoriesChange, onCategoryProductsLoad, isTargetPriceCampaign = false }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        setError('');
        const categoriesData = await getCategories();
        setCategories(categoriesData);
      } catch (err) {
        setError('حدث خطأ أثناء تحميل التصنيفات');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // دالة لجلب منتجات التصنيف
  const fetchCategoryProducts = async (categoryId: string) => {
    try {
      setLoadingProducts(true);
      const response = await axiosInstance.get(`/category-products/${categoryId}`);
      return response.data.data?.products || response.data.products || [];
    } catch (err) {
      console.error('خطأ في جلب منتجات التصنيف:', err);
      throw new Error('فشل في جلب منتجات التصنيف');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleCategoryToggle = async (categoryId: string) => {
    const isCurrentlySelected = selectedCategories.includes(categoryId);
    
    // إذا كانت حملة السعر المستهدف وتم اختيار تصنيف جديد
    if (isTargetPriceCampaign && !isCurrentlySelected && onCategoryProductsLoad) {
      try {
        setError('');
        console.log('🔍 جلب منتجات التصنيف:', categoryId);
        
        const products = await fetchCategoryProducts(categoryId);
        console.log('📦 تم جلب المنتجات:', products.length);
        
        if (products.length === 0) {
          setError('لا توجد منتجات في هذا التصنيف');
          return;
        }
        
        // تحويل المنتجات إلى تنسيق MarketingProductVariant
        const variants: MarketingProductVariant[] = products.map((product: any) => ({
          _id: product._id,
          productId: product.productId,
          productName: product.productName,
          sku: product.sku,
          barcode: product.barcode,
          attributes: product.attributes || [],
          price: product.price,
          // لا نضع targetPrice هنا - سيتم إدخاله يدوياً
        }));
        
        console.log('✅ تم تحويل المنتجات:', variants.length);
        
        // إضافة المنتجات إلى القائمة
        onCategoryProductsLoad(variants);
        
        // عدم إضافة التصنيف إلى القائمة المختارة في حملات السعر المستهدف
        // لأننا حولناه إلى منتجات فردية
        return;
        
      } catch (err: any) {
        console.error('❌ خطأ في جلب منتجات التصنيف:', err);
        setError(err.response?.data?.message || err.message || 'حدث خطأ أثناء جلب منتجات التصنيف');
        setLoadingProducts(false); // تأكد من إيقاف التحميل
        return;
      }
    }
    
    // السلوك العادي للحملات الأخرى
    const newSelectedCategories = isCurrentlySelected
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];
    
    onCategoriesChange(newSelectedCategories);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }} align="right">
        اختيار التصنيفات
      </Typography>
      
      {/* رسالة خاصة لحملات السعر المستهدف */}
      {isTargetPriceCampaign && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>ملاحظة:</strong> في حملات السعر المستهدف، سيتم تحويل التصنيفات المختارة تلقائياً إلى منتجات فردية لتتمكن من إدخال سعر مستهدف لكل منتج على حدة.
          </Typography>
        </Alert>
      )}
      
      {/* مؤشر تحميل المنتجات */}
      {loadingProducts && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              جاري تحميل منتجات التصنيف...
            </Typography>
          </Box>
        </Alert>
      )}
      
      {/* رسائل الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {error}
          </Typography>
        </Alert>
      )}
      
      {categories.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: 'grey.50',
            border: '1px dashed',
            borderColor: 'grey.300',
            borderRadius: 2
          }}
        >
          <CategoryIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            لا توجد تصنيفات متاحة
          </Typography>
        </Paper>
      ) : (
        <Paper variant="outlined" sx={{ maxHeight: 300, overflowY: 'auto' }}>
          <List dense>
            {categories.map((category) => (
              <ListItem key={category._id} sx={{ py: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedCategories.includes(category._id)}
                      onChange={() => handleCategoryToggle(category._id)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {category.name}
                      </Typography>
                      {category.description && (
                        <Typography variant="body2" color="text.secondary">
                          - {category.description}
                        </Typography>
                      )}
                    </Box>
                  }
                  sx={{ width: '100%', margin: 0, direction: 'rtl' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
      
      {selectedCategories.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            التصنيفات المختارة ({selectedCategories.length}):
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedCategories.map((categoryId) => {
              const category = categories.find(c => c._id === categoryId);
              return (
                <Chip
                  key={categoryId}
                  label={category?.name || categoryId}
                  onDelete={() => handleCategoryToggle(categoryId)}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

// مكون اختيار متغيرات المنتج (يجمع الطرق الأربع)
const ProductVariantSelector: React.FC<{
  onSelect: (variants: MarketingProductVariant[]) => void;
  initialVariants?: MarketingProductVariant[];
  selectedCategories?: string[];
  onCategoriesChange?: (categories: string[]) => void;
  onSave?: (variants: MarketingProductVariant[]) => void;
  onCancel?: () => void;
  isTargetPriceCampaign?: boolean;
}> = ({ onSelect, initialVariants = [], selectedCategories = [], onCategoriesChange, onSave, onCancel, isTargetPriceCampaign = false }) => {
  const [selectedVariants, setSelectedVariants] = useState<MarketingProductVariant[]>(initialVariants);
  const [tabValue, setTabValue] = useState<number>(0);
  const [localSelectedCategories, setLocalSelectedCategories] = useState<string[]>(selectedCategories);

  const addVariant = (variant: MarketingProductVariant) => {
    // التحقق من عدم تكرار المتغير
    if (!selectedVariants.some(v => v._id === variant._id)) {
      const newVariants = [...selectedVariants, variant];
      setSelectedVariants(newVariants);
      // لا نستدعي onSelect هنا لأننا نريد الحفظ يدوياً
    }
  };

  const addVariants = (variants: MarketingProductVariant[]) => {
    // إضافة المتغيرات الجديدة مع تجنب التكرار
    const uniqueVariants = variants.filter(
      variant => !selectedVariants.some(v => v._id === variant._id)
    );
    
    const newVariants = [...selectedVariants, ...uniqueVariants];
    setSelectedVariants(newVariants);
    // لا نستدعي onSelect هنا لأننا نريد الحفظ يدوياً
  };

  const removeVariant = (variantId: string) => {
    const newVariants = selectedVariants.filter(v => v._id !== variantId);
    setSelectedVariants(newVariants);
  };

  const handleSave = () => {
    onSelect(selectedVariants);
    if (onSave) {
      onSave(selectedVariants);
    }
    // إرسال التصنيفات المختارة أيضاً
    if (onCategoriesChange) {
      onCategoriesChange(localSelectedCategories);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const handleCategoriesChange = (categories: string[]) => {
    setLocalSelectedCategories(categories);
    if (onCategoriesChange) {
      onCategoriesChange(categories);
    }
  };

  // دالة معالجة تحميل منتجات التصنيف (للحملات السعر المستهدف)
  const handleCategoryProductsLoad = (variants: MarketingProductVariant[]) => {
    addVariants(variants);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* قسم الإدخال */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="طرق إدخال متغيرات المنتج"
          dir="rtl"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              fontSize: '1rem',
              fontWeight: 500,
              minHeight: 56
            }
          }}
        >
          <Tab label="إدخال الباركود" id="variant-tab-0" aria-controls="variant-tabpanel-0" />
          <Tab label="اختيار يدوي" id="variant-tab-1" aria-controls="variant-tabpanel-1" />
          <Tab label="استيراد من إكسل" id="variant-tab-2" aria-controls="variant-tabpanel-2" />
          <Tab label="اختيار التصنيفات" id="variant-tab-3" aria-controls="variant-tabpanel-3" />
        </Tabs>
      </Box>

      {/* محتوى التبويبات */}
      <Box sx={{ minHeight: 200, mb: 3 }}>
        <TabPanel value={tabValue} index={0}>
          <BarcodeProductInput onSelect={addVariant} />
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <ManualProductVariantSelector onSelect={addVariant} />
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <ExcelProductImporter onImport={addVariants} />
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <CategorySelector
            selectedCategories={localSelectedCategories}
            onCategoriesChange={handleCategoriesChange}
            onCategoryProductsLoad={handleCategoryProductsLoad}
            isTargetPriceCampaign={isTargetPriceCampaign}
          />
        </TabPanel>
      </Box>

      {/* قسم المنتجات المختارة */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, px: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }} align="right">
            المنتجات المختارة
          </Typography>
          <Chip
            label={`${selectedVariants.length} منتج`}
            color="primary"
            variant="outlined"
            size="medium"
          />
        </Box>
        
        {selectedVariants.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: 'grey.50',
              border: '1px dashed',
              borderColor: 'grey.300',
              borderRadius: 2
            }}
          >
            <InventoryIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              لم يتم اختيار أي منتجات بعد
            </Typography>
            <Typography variant="body2" color="text.secondary">
              استخدم إحدى الطرق أعلاه لإضافة منتجات إلى الحملة التسويقية
            </Typography>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: 300 }}>
            <List dense>
              {selectedVariants.map((variant, index) => (
                <ListItem
                  key={variant._id}
                  divider={index < selectedVariants.length - 1}
                  sx={{ py: 1.5, alignItems: 'flex-start' }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.95rem',
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          lineHeight: 1.4
                        }}
                      >
                        {`${variant.barcode || 'غير محدد'} - ${variant.productName || variant.sku || 'غير محدد'} - ${
                          variant.attributes && variant.attributes.length > 0
                            ? variant.attributes.map(attr => `${attr.name}: ${attr.value}`).join(', ')
                            : 'لا توجد خصائص'
                        }`}
                      </Typography>
                    }
                    style={{ textAlign: 'right', paddingRight: '48px' }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => removeVariant(variant._id)}
                      size="small"
                      sx={{
                        '&:hover': {
                          bgcolor: 'error.light',
                          color: 'white'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>
      
      {/* أزرار الحفظ والإلغاء */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2,
        mt: 3,
        pt: 2,
        borderTop: 1,
        borderColor: 'divider'
      }}>
        <Button
          variant="outlined"
          onClick={handleCancel}
          disabled={!onCancel}
          size="large"
          sx={{ minWidth: 120 }}
        >
          إلغاء
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          startIcon={<CheckCircleIcon />}
          size="large"
          sx={{ minWidth: 200 }}
        >
          حفظ المنتجات ({selectedVariants.length})
        </Button>
      </Box>
    </Box>
  );
};

export default ProductVariantSelector;